import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/text-button.js";
import "@material/web/icon/icon.js";

import "../components/app-shell";
import { pb } from "../lib/pb";
import { sharedStyles } from "../styles/shared";
import { errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import type { AuditLogEntry } from "../lib/types";

const PAGE_SIZE = 25;

/** Splits a PascalCase event name into space-separated words, e.g.
 * "ProductCreated" -> "Product Created", "UPIAssigned" -> "UPI Assigned". */
function splitWords(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

/** Formats a platform event type like "catalog.ProductCreated" into
 * "Catalog · Product Created". */
function formatEventType(eventType: string): string {
  const [moduleName, name] = eventType.split(".");
  if (!moduleName || !name) return eventType;
  const label = `${moduleName.charAt(0).toUpperCase()}${moduleName.slice(1)}`;
  return `${label} · ${splitWords(name)}`;
}

/** Icon + tint for an event's timeline roundel, keyed by originating module. */
function eventVisual(eventType: string): { icon: string; tone: string } {
  const [moduleName, name] = eventType.split(".");
  const bad = /failed|revoked|suspended|flagged|deactivated/i.test(
    eventType,
  );
  const tone = bad
    ? "var(--md-sys-color-error)"
    : /created|published|verified|assigned|authorized/i.test(name ?? "")
      ? "var(--md-sys-color-primary)"
      : "var(--md-sys-color-outline)";
  const icons: Record<string, string> = {
    nfc: "nfc",
    catalog: "inventory_2",
    dpp: "description",
    identifier: "link",
    tenancy: "person_add",
    giregistry: "workspace_premium",
    geocoding: "location_on",
    mapservice: "map",
    audit: "receipt_long",
  };
  return { icon: icons[moduleName] ?? "receipt_long", tone };
}

@customElement("audit-page")
export class AuditPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .feed {
        padding: 8px 0;
      }

      .entry {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        padding: 14px 20px;
        border-bottom: 1px solid var(--md-sys-color-outline-variant);
      }

      .entry:last-child {
        border-bottom: none;
      }

      .roundel {
        width: 36px;
        height: 36px;
        border-radius: 999px;
        flex: none;
        background: var(--md-sys-color-surface-container-high);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        --md-icon-size: 18px;
      }

      .entry__body {
        flex: 1;
        min-width: 0;
        font: 400 14px/1.45 var(--font-plain, sans-serif);
      }

      .entry__when {
        font: 400 12px/1.3 var(--font-plain, sans-serif);
        color: var(--md-sys-color-on-surface-variant);
        margin-top: 2px;
      }

      details > summary {
        cursor: pointer;
        color: var(--md-sys-color-primary);
        font-size: 0.8125rem;
        font-weight: 500;
        margin-top: 4px;
      }

      details pre {
        margin: 8px 0 0;
        padding: 12px;
        background: var(--md-sys-color-surface-container-high);
        border-radius: 8px;
        font-size: 0.75rem;
        overflow-x: auto;
      }

      .pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 16px;
        padding: 12px 16px;
        border-top: 1px solid var(--md-sys-color-outline-variant);
      }

      .pagination span {
        font-size: 0.8125rem;
        color: var(--md-sys-color-on-surface-variant);
      }
    `,
  ];

  @state() private entries: AuditLogEntry[] = [];
  @state() private page = 1;
  @state() private totalPages = 1;
  @state() private loading = true;
  @state() private error = "";

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const result = await pb
        .collection("audit_log")
        .getList(this.page, PAGE_SIZE, { sort: "-occurred_at" });
      this.entries = result.items as unknown as AuditLogEntry[];
      this.totalPages = result.totalPages || 1;
    } catch (err) {
      this.error = errorMessage(err, "Failed to load the audit log.");
    } finally {
      this.loading = false;
    }
  }

  private goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) return;
    this.page = page;
    void this.load();
  }

  render() {
    return html`
      <app-shell active="/audit">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Audit log</h1>
              <p>
                A record of significant events across your organization's
                Digital Product Passports.
              </p>
            </div>
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : this.entries.length === 0
                ? html`<div class="card card--flush empty">
                    No events recorded yet.
                  </div>`
                : html`
                    <div class="card card--flush">
                      <div class="feed">
                        ${this.entries.map((entry) => {
                          const v = eventVisual(entry.event_type);
                          return html`
                            <div class="entry">
                              <span class="roundel" style="color:${v.tone}"
                                >${icon(v.icon)}</span
                              >
                              <div class="entry__body">
                                <b>${formatEventType(entry.event_type)}</b>
                                <div class="entry__when">
                                  ${new Date(
                                    entry.occurred_at,
                                  ).toLocaleString()}
                                </div>
                                <details>
                                  <summary>View payload</summary>
                                  <pre>
${JSON.stringify(entry.payload, null, 2)}</pre
                                  >
                                </details>
                              </div>
                            </div>
                          `;
                        })}
                      </div>
                      ${this.totalPages > 1
                        ? html`
                            <div class="pagination">
                              <md-text-button
                                ?disabled=${this.page <= 1}
                                @click=${() => this.goToPage(this.page - 1)}
                              >
                                Previous
                              </md-text-button>
                              <span
                                >Page ${this.page} of ${this.totalPages}</span
                              >
                              <md-text-button
                                ?disabled=${this.page >= this.totalPages}
                                @click=${() => this.goToPage(this.page + 1)}
                              >
                                Next
                              </md-text-button>
                            </div>
                          `
                        : null}
                    </div>
                  `}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "audit-page": AuditPage;
  }
}
